import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import chroma from 'chroma-js';
import * as MuiIcons from '@mui/icons-material';

const OrganizationChart = ({ structures, mainColor, secColor }) => {
  const theme = useTheme();

  // Create color variations for each level with more contrast
  const generateColorPalette = (baseColor) => {
    if (!baseColor) return [];

    return [
      chroma(baseColor).brighten(0.8).hex(), // Level 1 - Much brighter
      chroma(baseColor).brighten(0.4).hex(), // Level 2 - Brighter
      baseColor, // Level 3 - Base
      chroma(baseColor).darken(0.4).hex(), // Level 4 - Darker
      chroma(baseColor).darken(0.8).hex(), // Level 5 - Much darker
    ];
  };

  const colorPalette = generateColorPalette(
    mainColor || theme.palette.primary.main
  );

  // Function to get the appropriate icon component
  const getIconComponent = (iconName) => {
    if (!iconName) return MuiIcons.Folder;
    const IconComponent = MuiIcons[iconName];
    return IconComponent || MuiIcons.Folder;
  };

  // Function to build the tree structure from paths
  const buildTree = (structures) => {
    const tree = {};

    structures.forEach((structure) => {
      if (!structure.paths) return;

      structure.paths.forEach((path) => {
        const parts = path.split('/');
        let current = tree;

        parts.forEach((part, index) => {
          if (!current[part]) {
            const structureData = structures.find((s) => s.id === part);
            current[part] = {
              id: part,
              children: {},
              name:
                structureData?.name_fr ||
                structureData?.name_en ||
                structureData?.name ||
                part,
              level: index,
              icon: structureData?.icon || 'Folder',
            };
          }
          current = current[part].children;
        });
      });
    });

    return tree;
  };

  // Function to render a node and its children
  const renderNode = (node, level = 0) => {
    const levelColor = colorPalette[Math.min(level, colorPalette.length - 1)];
    const isLight = chroma(levelColor).luminance() > 0.5;
    const IconComponent = getIconComponent(node.icon);

    return (
      <Box key={node.id} sx={{ ml: level * 4 }}>
        <Paper
          elevation={2}
          sx={{
            p: 1,
            mb: 2,
            backgroundColor: 'white',
            color: 'black',
            border: `2px solid ${levelColor}`,
            borderLeft: `6px solid ${levelColor}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateX(5px)',
              boxShadow: 4,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconComponent sx={{ fontSize: '1.1rem', color: levelColor }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '1rem',
                color: 'black',
              }}
            >
              {node.name}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 400,
                fontSize: '0.7rem',
                color: 'black',
              }}
            >
              #{node?.id?.slice(0, 6)}
            </Typography>
          </Box>
        </Paper>
        {Object.values(node.children).map((child) =>
          renderNode(child, level + 1)
        )}
      </Box>
    );
  };

  const tree = buildTree(structures);

  return (
    <Box sx={{ p: 2 }}>
      {Object.values(tree).map((node) => renderNode(node))}
    </Box>
  );
};

export default OrganizationChart;
